import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { ExternalReportFields } from '@/components/backend/form/ExternalReportFields';
import { AdminLevelUsersTable } from '@/components/backend/table/AdminLevelUsersTable';
import { AdminLevelsTable } from '@/components/backend/table/AdminLevelsTable';
import { AlertWithTitle } from '@/components/content/AlertWithTitle';
import { getI18nRadioLabel } from '@/components/form-elements/I18nRadioLabel';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { useAbility } from '@/hooks/useAbility';
import { I18N_DE } from '@/i18n/de';
import { EMAIL_PATTERN } from '@/modules/EMAIL_PATTERN';
import { UserGuard } from '@/modules/UserGuard';
import { Action, AdminLevelType, Subject, UserNotificationInterval } from '@/modules/api';
import { backendApiAdminLevelsUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useAdminLevelSuspenseQuery } from '@/queries/useAdminLevelSuspenseQuery';
import { useUserSuspenseQuery } from '@/queries/useUserSuspenseQuery';
import { AdminLevelActive } from '@/types/AdminLevelActive';
import { ManagedByParent } from '@/types/ManagedByParent';

export const Route = createFileRoute('/verwaltung/$adminLevelId/')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const { data, isFetching } = useAdminLevelSuspenseQuery();
  const { data: user } = useUserSuspenseQuery();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const formMethods = useForm<{
    active: AdminLevelActive;
    managedByParent: ManagedByParent;
    email: string;
    externalReportPlatformUrl: string;
    externalReportEndpointUrl: string;
    externalReportJurisdictionId: string;
    sharedEmail: string;
    sharedNotificationInterval: UserNotificationInterval;
  }>();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors, isDirty },
  } = formMethods;

  const watchAdminLevelActive = watch('active');
  const watchManagedByParent = watch('managedByParent');

  useEffect(() => {
    if (data !== undefined) {
      reset({
        active: data.active ? AdminLevelActive.TRUE : AdminLevelActive.FALSE,
        managedByParent: data.managedByParent ? ManagedByParent.TRUE : ManagedByParent.FALSE,
        email: data.email ?? '',
        externalReportPlatformUrl: data.externalReportPlatformUrl ?? '',
        externalReportEndpointUrl: data.externalReportEndpointUrl ?? '',
        externalReportJurisdictionId: data.externalReportJurisdictionId ?? '',
        sharedEmail: data.sharedEmail ?? '',
        sharedNotificationInterval: data.sharedNotificationInterval,
      });
    }
  }, [data, reset]);

  const { mutateAsync, isError } = useMutation({
    ...backendApiAdminLevelsUpdateMutation(),
  });

  const { ability } = useAbility();

  const canUpdateManagedByParent = ability.can('UPDATE', data, 'managedByParent');

  const form = (
    <FormProvider {...formMethods}>
      <Stack
        component="form"
        gap={2}
        onSubmit={handleSubmit(async (formData) => {
          await mutateAsync({
            path: {
              adminLevelId: data.id,
            },
            body: {
              active: formData.active === AdminLevelActive.TRUE,
              managedByParent: formData.managedByParent === ManagedByParent.TRUE,
              sharedEmail: formData.sharedEmail || null,
              sharedNotificationInterval: formData.sharedNotificationInterval,
              ...(formData.active === AdminLevelActive.FALSE && {
                email: formData.email || null,
                externalReportPlatformUrl: formData.externalReportPlatformUrl || null,
                externalReportEndpointUrl: formData.externalReportEndpointUrl.endsWith('/')
                  ? formData.externalReportEndpointUrl.slice(0, -1)
                  : formData.externalReportEndpointUrl || null,
                externalReportJurisdictionId: formData.externalReportJurisdictionId || null,
              }),
            },
          });
        })}
      >
        <Typography level="h3">Verwaltungsebene</Typography>

        <FormControl>
          <FormLabel>Name</FormLabel>
          <Input readOnly value={data?.name ?? ''} />
        </FormControl>

        <FormControl>
          <FormLabel>Ebene</FormLabel>
          <Input readOnly value={(data && I18N_DE.AdminLevelType[data.type]) ?? ''} />
        </FormControl>

        <FormControl>
          <FormLabel>Status</FormLabel>
          <Controller
            control={control}
            name="active"
            render={({ field }) => (
              <RadioWithDescriptionGroup
                {...field}
                options={Object.values(AdminLevelActive)}
                optionLabel={getI18nRadioLabel({
                  i18nTitle: I18N_DE.AdminLevelActive,
                  i18nDescription: I18N_DE.AdminLevelActiveDescription,
                })}
              />
            )}
          />
        </FormControl>
        {data.type === 'COUNTY' && data.active && watchAdminLevelActive === AdminLevelActive.FALSE ? (
          <AlertWithTitle>
            Wenn Landkreis auf <b>{I18N_DE.AdminLevelActive.FALSE}</b> gesetzt wird, werden auch alle untergeordneten
            Kommunen mit Zuständigkeit <b>{I18N_DE.ManagedByParent.TRUE}</b> auf Inaktiv gesetzt.
          </AlertWithTitle>
        ) : null}
        {data.type === 'COUNTY' && !data.active && watchAdminLevelActive === AdminLevelActive.TRUE ? (
          <AlertWithTitle>
            Alle untergeordnete Kommunen mit Zuständigkeit <b>{I18N_DE.ManagedByParent.TRUE}</b> werden auch automatisch
            auf <b>{I18N_DE.AdminLevelActive.TRUE}</b> gesetzt.
          </AlertWithTitle>
        ) : null}

        {data.type === 'MUNICIPALITY' && (
          <Fragment>
            <FormControl>
              <FormLabel>Zuständigkeit</FormLabel>
              <Controller
                control={control}
                name="managedByParent"
                render={({ field }) => (
                  <RadioWithDescriptionGroup
                    {...field}
                    {...(!canUpdateManagedByParent && {
                      disabledOptions: Object.values(ManagedByParent).filter((value) => value !== field.value),
                    })}
                    options={Object.values(ManagedByParent)}
                    optionLabel={getI18nRadioLabel({
                      i18nTitle: I18N_DE.ManagedByParent,
                      i18nDescription: I18N_DE.ManagedByParentDescription,
                    })}
                  />
                )}
              />
            </FormControl>
            {!canUpdateManagedByParent && (
              <AlertWithTitle title="Änderung der Zuständigkeit">
                Die Änderung der Zuständigkeit deiner Kommune muss vom Landkreis {data.parent?.name} durchgeführt
                werden.
              </AlertWithTitle>
            )}
            {canUpdateManagedByParent &&
            watchManagedByParent === ManagedByParent.TRUE &&
            watchAdminLevelActive === AdminLevelActive.FALSE ? (
              <AlertWithTitle title="Verwaltungsebene inaktiv">
                Die Verwaltungsebene ist <b>{I18N_DE.AdminLevelActive.FALSE}</b>. Die Funktion der übergeordneten
                Zuständigkeit verlangt ein <b>{I18N_DE.AdminLevelActive.TRUE}</b>-Setzen der Verwaltungsebene.
              </AlertWithTitle>
            ) : null}
          </Fragment>
        )}

        {watchAdminLevelActive === AdminLevelActive.TRUE && (
          <Fragment>
            <FormControl error={!!errors.sharedEmail}>
              <FormLabel>Sammelpostfach</FormLabel>
              <Controller
                control={control}
                name="sharedEmail"
                render={({ field }) => <Input {...field} placeholder="E-Mail" type="email" />}
                rules={{
                  pattern: EMAIL_PATTERN,
                }}
              />
              {errors.sharedEmail ? <FormHelperText>{errors.sharedEmail.message}</FormHelperText> : null}
            </FormControl>

            <FormControl>
              <FormLabel>Benachrichtigungsintervall Sammelpostfach</FormLabel>
              <Controller
                control={control}
                defaultValue={data.sharedNotificationInterval}
                name="sharedNotificationInterval"
                render={({ field }) => (
                  <Select {...field} onChange={(_, value) => field.onChange(value)}>
                    {Object.values(UserNotificationInterval).map((userNotificationInterval) => (
                      <Option key={userNotificationInterval} value={userNotificationInterval}>
                        {I18N_DE.UserNotificationInterval[userNotificationInterval]}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Fragment>
        )}

        {(data?.type === AdminLevelType.MUNICIPALITY || data?.type === AdminLevelType.CITY_COUNTY) &&
          watchAdminLevelActive === AdminLevelActive.FALSE && <ExternalReportFields />}

        <Stack direction="row" gap={1} justifyContent="flex-end" marginTop={4}>
          <Button disabled={!isDirty} type="submit">
            Speichern
          </Button>
        </Stack>
        {isError ? <Alert color="danger">Es ist ein Fehler aufgetreten</Alert> : null}
      </Stack>
    </FormProvider>
  );

  return (
    <Grid container spacing={4}>
      <Grid md={6}>{form}</Grid>

      <Grid md={6}>
        <Typography level="h3" marginBottom={6}>
          Zugeordnete Benutzer*innen
        </Typography>
        <AdminLevelUsersTable />

        {ability?.can(Action.READ_ALL, Subject.AdminLevel) ||
        (data.type === 'COUNTY' &&
          UserGuard.isUser(user) &&
          user.adminLevels?.find(
            ({ adminLevelId, role, status }) => adminLevelId === data.id && role === 'MANAGER' && status === 'ACTIVE',
          )) ? (
          <Stack gap={2} marginTop={4}>
            <Stack alignItems="center" direction="row" justifyContent="space-between">
              <Typography level="h3">Untergeordnete Verwaltungsebenen</Typography>
              {data.active &&
              data.type === 'COUNTY' &&
              data.children?.find(
                (childAdminLevel) =>
                  !childAdminLevel.active &&
                  !childAdminLevel.externalReportEndpointUrl &&
                  !childAdminLevel.externalReportPlatformUrl,
              ) ? (
                <Fragment>
                  <Button onClick={() => setConfirmOpen(true)} variant="outlined">
                    Alle aktivieren
                  </Button>
                  <ConfirmationModal
                    content="Alle kreisangehörigen inaktiven Gemeinden (ohne URL, ohne Schnittstelle) aktivieren und für übergeordnete Meldungsbearbeitung durch Landkreis übernehmen."
                    onAbort={() => setConfirmOpen(false)}
                    open={confirmOpen}
                    title="Alle Gemeinden aktivieren"
                    onConfirm={async () => {
                      setConfirmOpen(false);
                      await mutateAsync({
                        path: {
                          adminLevelId: data.id,
                        },
                        body: {
                          activateManagedChildren: true,
                        },
                      });
                    }}
                  />
                </Fragment>
              ) : null}
            </Stack>

            <AdminLevelsTable hideEditors hideManagers hideParent data={data?.children} isFetching={isFetching} />
          </Stack>
        ) : null}
      </Grid>
    </Grid>
  );
}
